import React, { useState, useEffect } from "react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { Phone, Instagram, MessageCircle, Globe, LogOut } from "lucide-react";
import { db } from "../Config/config";
import { useAuth } from "./AuthContext";
import styled from "styled-components";
import avatar1 from "../Avatars/1.jpg";
import avatar2 from "../Avatars/2.jpg";
import avatar3 from "../Avatars/3.jpg";
import avatar4 from "../Avatars/4.jpg";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import planetsAnimation from "../Animations/planets.json";
import TravelRequest from "./Request";

const Account = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const calculateValidityDate = (purchaseDate, duration) => {
    if (!purchaseDate || !duration) return null;

    let years = 0;
    if (
      duration.includes("LIFE TIME") ||
      duration.toLowerCase().includes("lifetime")
    ) {
      years = 20;
    } else {
      const match = duration.match(/\d+/);
      if (match) {
        years = parseInt(match[0]);
      }
    }

    const date = purchaseDate.toDate();
    date.setFullYear(date.getFullYear() + years);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          }

          const purchasesRef = collection(db, "users", user.uid, "purchases");
          const snapshot = await getDocs(purchasesRef);
          const purchasesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPurchases(purchasesData);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const getAvatarImage = (avatarNumber) => {
    switch (avatarNumber) {
      case "1":
        return avatar1;
      case "2":
        return avatar2;
      case "3":
        return avatar3;
      case "4":
        return avatar4;
      default:
        return "https://via.placeholder.com/120";
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const PackageCard = ({ purchase }) => {
    const [showTravelRequest, setShowTravelRequest] = useState(false);
  
    return (
      <Card $isRejected={purchase.status?.toUpperCase() === "REJECTED"}>
        <TagContainer>
          <ActiveTag $status={purchase.status}>
            {purchase.status?.toUpperCase() || "PENDING"}
          </ActiveTag>
        </TagContainer>
        <LottieContainer>
          <Lottie
            animationData={planetsAnimation}
            style={{ width: "100%", height: "100%" }}
          />
        </LottieContainer>
        <CardContent>
          <PackageInfo>
            <PackageText>{purchase.packageName}</PackageText>
            <PackageText>
              Validity -{" "}
              {calculateValidityDate(
                purchase.properPurchaseDate,
                purchase.duration
              )}
            </PackageText>
          </PackageInfo>
          <RaiseTravelButton
            disabled={purchase.status?.toUpperCase() === "REJECTED"}
            onClick={() => setShowTravelRequest(true)}
          >
            <RaiseTravelText>Raise Travel Request</RaiseTravelText>
          </RaiseTravelButton>
        </CardContent>
        
        {showTravelRequest && (
          <TravelRequest 
            purchase={purchase}
            onClose={() => setShowTravelRequest(false)}
          />
        )}
      </Card>
    );
  };
  if (loading) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  if (!user) {
    return <ErrorContainer>Please log in to view your account.</ErrorContainer>;
  }

  const SocialButton = ({ Icon, link, onClick }) => (
    <SocialButtonStyled
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      <Icon color="#441B77" size={24} />
    </SocialButtonStyled>
  );

  return (
    <PageContainer>
      <TopSection>
        <DesktopLayout>
          <LeftSection>
            <ProfileContainer>
              <ImageContainer>
                <Avatar
                  src={getAvatarImage(userData?.avatarSelected)}
                  alt="User Avatar"
                />
              </ImageContainer>
              <Name>{userData?.name || "User Name"}</Name>
            </ProfileContainer>
            <ButtonContainer>
              <SocialButtonsRow>
                <SocialButton Icon={Phone} link={`tel:${userData?.number}`} />
                <SocialButton Icon={Instagram} link={userData?.instagram} />
                <SocialButton
                  Icon={MessageCircle}
                  link={`https://wa.me/${userData?.number}`}
                />
                <SocialButton Icon={Globe} link={userData?.website} />
              </SocialButtonsRow>
              <LogoutButton onClick={handleLogout}>
                <LogOut size={24} />
                <span>Logout</span>
              </LogoutButton>
            </ButtonContainer>
          </LeftSection>
        </DesktopLayout>
      </TopSection>
      <PackagesSection>
        <PackagesHeading>My Packages</PackagesHeading>
        <PackagesContainer>
          {purchases.map((purchase) => (
            <PackageCard key={purchase.id} purchase={purchase} />
          ))}
          {purchases.length === 0 && (
            <EmptyStateMessage>No packages purchased yet.</EmptyStateMessage>
          )}
        </PackagesContainer>
      </PackagesSection>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const TopSection = styled.div`
  background: linear-gradient(to bottom, #a5204c, #441B77);
  padding: 20px;
  min-height: 60vh;
  display: flex;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    min-height: 50vh;
    padding-top: 12vh;
    align-items: flex-start;
  }
`;

const DesktopLayout = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  height: 100%;
  display: flex;
  align-items: center;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 40px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 20%;
  gap: 15px;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    align-items: center;
    justify-content: center;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  margin-left: 10%;
  margin-top: 5%;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: -25px;
    width: 85%;
    
  }
`;

const SocialButtonsRow = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Avatar = styled.img`
  height: 120px;
  width: 120px;
  object-fit: cover;
  border-radius: 50%;
`;

const ImageContainer = styled.div`
  border: 4px solid white;
  border-radius: 50%;
  padding: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const Name = styled.span`
  color: #441B77;
  font-family: "Poppins-SemiBold", sans-serif;
  font-size: 18px;
  background-color: white;
  padding: 4px 15px;
  border-radius: 50px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SocialButtonStyled = styled.a`
  height: 50px;
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  color: #441B77;
  font-family: "Poppins-SemiBold", sans-serif;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  margin-top: 15px;

  &:hover {
    background-color: #f0f0f0;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  span {
    font-size: 14px;
  }
`;

const PackagesSection = styled.div`
  padding: 20px;
  margin-top: -50px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 1;
`;

const PackagesHeading = styled.h2`
  font-family: "Poppins-SemiBold", sans-serif;
  color: #441B77;
  font-size: 24px;
  margin-bottom: 20px;
  padding-left: 10px;
`;

const PackagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  padding: 10px;
`;

const Card = styled.div`
  background-color: black;
  border-radius: 10px;
  padding: 15px;
  height: 300px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  opacity: ${(props) => (props.$isRejected ? 0.7 : 1)};
  transition: opacity 0.3s ease;
`;

const TagContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActiveTag = styled.span`
  background-color: ${(props) => {
    switch (props.$status?.toUpperCase()) {
      case "REJECTED":
        return "#ff4444";
      case "PENDING":
        return "orange";
      case "ACTIVE":
        return "green";
      default:
        return "red";
    }
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: "Poppins-Medium", sans-serif;
  font-size: 12px;
`;

const LottieContainer = styled.div`
  height: 180px;
  margin: 10px 0;
`;

const CardContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const PackageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const PackageText = styled.span`
  background-color: #fefbbd;
  color: #441B77;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: "Poppins-SemiBold", sans-serif;
  font-size: 12px;
`;

const RaiseTravelButton = styled.button`
  background-color: #441B77;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${(props) => (props.disabled ? "#441B77" : "#641029")};
    transform: ${(props) => (props.disabled ? "none" : "translateY(-1px)")};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "translateY(0)")};
  }
`;

const RaiseTravelText = styled.span`
  color: white;
  font-family: "Poppins-SemiBold", sans-serif;
  font-size: 12px;
  white-space: nowrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: #441B77;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 16px;
  color: #441B77;
  text-align: center;
  padding: 0 20px;
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  color: #441B77;
  font-family: "Poppins-Medium", sans-serif;
  padding: 20px;
  grid-column: 1 / -1;
  font-size: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;
export default Account;
